import {
    Component,
    ChangeDetectionStrategy,
    computed,
    DestroyRef,
    effect,
    EventEmitter,
    inject,
    OnInit,
    Output,
    Signal,
    signal,
    viewChildren,
    WritableSignal,
    ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { CreateOrUpdateStandaloneInvoiceStore } from './create-or-update-standalone-invoice.store';
import { CreateInvoiceCharge, StandaloneInvoiceDraft } from './types/StandaloneInvoiceDraft';
import {
    ICreateInvoiceCommandDto,
    ICreateInvoiceItemCommandDto,
    UpdateAccountingRequestDto,
} from './types/ICreateStandaloneInvoiceCommand';
import { BC, BreadcrumbService } from '@lib/shared/breadcrumbs';
import {
    AbstractControl,
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import {
    BaseSiteLayoutModule,
    BootstrapColors,
    ConfirmationService,
    DialogOptions,
    DisplayCurrencyModule,
    Logger,
    MobileModule,
    MobileService,
} from '@lib/util';
import {
    IimAutocompleteModule,
    IimCardModule,
    IimDatepickerModule,
    IimDecimalInputV2Component,
    IimFilterableRecord,
    IimMarkRequiredModule,
    IimSpinnerModule,
} from '@iim-angular/components';
import { TranslateModule } from '@ngx-translate/core';
import {
    BottomSheetService,
    CopyToClipboardModule,
    CustomErrorMessages,
    FileDownloadModule,
    HighlightValidationError,
    IUploadBottomSheetOptions,
    isPdf,
    PdfPreviewComponent,
    ScannedImageComponent,
    UploadBottomSheetResult,
    ValidationErrorIndicatorDirective,
    View,
    NotificationService,
    NotificationMessageLevel,
} from '@lib/core';
import { finalize, take } from 'rxjs/operators';
import { SupplierDisplayNamePipe, SupplierService, SupplierSearchService, supplierDisplayName } from '@lib/supplier';
import {
    AddressService,
    AddressType,
    CompanyAddressModule,
    CompanyAddressWithContact,
    ICustomerAddressDto,
    InvoiceAddressDataService,
} from '@lib/address';
import { UnitSelectionModule } from '@lib/unit';
import { TextModuleCategoryEnum, TextModuleSelectionComponent } from '@lib/shared/text-module';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Accounting, AccountingChange, AccountingSelectionInput, AccountingType } from '@lib/accounting/types';
import { AccountingSelectionModule, AccountingSelectionService } from '@lib/accounting/selection';
import dayjs, { Dayjs } from 'dayjs';
import { IDeliveryRelation, IOrderRelation } from './types/IInvoiceDraftDto';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
    DeliveryReceiptDataService,
    DeliveryReceiptDetailComponent,
    DeliveryReceiptDetailDialogData,
} from '@lib/delivery-receipt/data-access';
import { firstValueFrom } from 'rxjs';
import { InvoiceNumberValidatorService } from '../util/invoice-number-validator.service';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { CHARGE_TYPE_OPTIONS, ChargeTypeEnum, IChargeTypeOption } from './types/ChargeType.enum';
import { MatSelectModule } from '@angular/material/select';
import { InvoiceStatus } from '@lib/invoice-public';
import { IMergedDeliveryItemData } from './types/IMergedDeliveryItemData';
import { IOrderItemViewDto, OrderDataService } from '@lib/order/data-access';
import { MaterialGroupSelectionComponent } from '@lib/material-group/selection';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatRipple } from '@angular/material/core';
import {
    LinkDeliveryReceiptDialogComponent,
    LinkDeliveryReceiptDialogData,
    LinkDeliveryReceiptDialogResult,
} from './link-delivery-receipt-dialog/link-delivery-receipt-dialog.component';
import {
    LinkOrderDialogComponent,
    LinkOrderDialogData,
    LinkOrderDialogResult,
} from '../detail/link-order-dialog/link-order-dialog.component';
import { DepartmentService } from '@lib/department/ui';
import { InvoiceCreationType } from './types/InvoiceCreationType';
import { InvoiceSettingsDto } from '../types/InvoiceSettingsDto';
import { CompanyCodeDataService, ICompanyCode } from '@lib/company-code';
import { IMoney } from '../types/IMoney';

// Form interfaces
interface ICreateStandaloneInvoiceForm {
    head: FormGroup<ICreateStandaloneInvoiceHeadForm>;
    items: FormArray<FormGroup<ICreateStandaloneInvoiceItemForm>>;
    charges: FormArray<FormGroup<ICreateInvoiceChargesForm>>;
    comment: FormControl<string>;
}

interface ICreateStandaloneInvoiceHeadForm {
    invoiceNumber: FormControl<string>;
    documentDate: FormControl<string>;
    paymentDate: FormControl<string>;
    paymentUntil: FormControl<string>;
    currency: FormControl<IimFilterableRecord<string>>;
    remark: FormControl<string>;
    files: FormControl<FormData>;
    scannedImages: FormControl<FormData>;
}

// materialGroupCode holds the material group business code (e.g. 7050101), matching the backend
// `materialGroupCode` wire field and the material-group-selection ControlValueAccessor contract.
interface ICreateStandaloneInvoiceItemForm {
    id: FormControl<string>;
    supplierArticleNumber: FormControl<string>;
    name: FormControl<string>;
    description: FormControl<string>;
    materialGroupCode: FormControl<string>;
    productUnit: FormControl<string>;
    contentUnitsPerOrderUnit: FormControl<number>;
    contentUnit: FormControl<string>;
    unitPrice: FormControl<number>;
    quantity: FormControl<number>;
    discountPercentage: FormControl<number>;
    taxInPercent: FormControl<number>;
    priceQuantity: FormControl<number>;
    deliveryRelation: FormControl<IDeliveryRelation | undefined>;
    orderRelation: FormControl<IOrderRelation | undefined>;
}

interface ICreateInvoiceChargesForm {
    name: FormControl<string>;
    description: FormControl<string>;
    articleNumberSupplier: FormControl<string>;
    articleNumberCustomer: FormControl<string>;
    price: FormControl<number>;
    taxInPercent: FormControl<number>;
    currency: FormControl<IimFilterableRecord<string>>;
    internalNote: FormControl<string>;
    chargeType: FormControl<string>;
}

interface IInvoiceTaxSummary {
    readonly percent: number;
    readonly amount: number;
}

interface IInvoiceTotals {
    readonly netAmount: number;
    readonly taxes: readonly IInvoiceTaxSummary[];
    readonly grossAmount: number;
    readonly currency: string;
}

export class InvoiceEditDialogData {
    constructor(
        public readonly invoiceId: string,
        public readonly taskId: string,
    ) {}
}

@Component({
    selector: 'create-standalone-invoice',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        DisplayCurrencyModule,
        FormsModule,
        ReactiveFormsModule,
        BaseSiteLayoutModule,
        IimCardModule,
        IimDatepickerModule,
        IimMarkRequiredModule,
        TranslateModule,
        ValidationErrorIndicatorDirective,
        MobileModule,
        IimDecimalInputV2Component,
        IimAutocompleteModule,
        IimSpinnerModule,
        CompanyAddressModule,
        UnitSelectionModule,
        FileDownloadModule,
        ScannedImageComponent,
        PdfPreviewComponent,
        AccountingSelectionModule,
        CopyToClipboardModule,
        IimMarkRequiredModule,
        ValidationErrorIndicatorDirective,
        HighlightValidationError,
        MatSelectModule,
        RouterLink,
        MatTooltipModule,
        MatRipple,
        TextModuleSelectionComponent,
        MaterialGroupSelectionComponent,
        SupplierDisplayNamePipe,
    ],
    templateUrl: './create-or-update-standalone-invoice.component.html',
    styleUrl: './create-or-update-standalone-invoice.component.scss',
    providers: [CreateOrUpdateStandaloneInvoiceStore, InvoiceNumberValidatorService],
})
export class CreateOrUpdateStandaloneInvoiceComponent implements OnInit {
    @Output() public invoiceChanged: EventEmitter<void> = new EventEmitter<void>();
    public static readonly ISO_DATE_TIME = 'YYYY-MM-DDTHH:mm:ss';

    public readonly decimalInputs: Signal<readonly IimDecimalInputV2Component[]> = viewChildren(IimDecimalInputV2Component);

    protected readonly InvoiceStatus = InvoiceStatus;

    textModuleCategory = TextModuleCategoryEnum;

    public readonly chargeTypeOptions: ReadonlyArray<IChargeTypeOption> = CHARGE_TYPE_OPTIONS;

    // Computed signal to get the invoice from the store
    public invoice = computed(() => this._store.invoice());
    public existingInvoiceId = computed(() => this._store.invoiceId());
    public readonly taskId = computed(() => this._store.taskId());
    public fixedSuppler = computed(() => this._store.fixedSupplier());
    public fixedDepartment = computed(() => this._store.fixedDepartment());
    public readonly allowSavingWithMissingFields = computed(() => !!this.taskId());

    // Computed signal to get the loading state from the store
    public isLoading = computed(() => this._store.isLoading());

    // Computed signal to get the currencies from the store
    public activeCurrencies = computed(() => this._store.currencies());

    // Computed signal to get the invoice settings from the store
    public invoiceSettings = computed(() => this._store.invoiceSettings());

    public departments = computed(() => this._store.departments());

    public readonly companyCodes = signal<readonly ICompanyCode[]>([]);
    private readonly companyCodeOptionsCache = signal<readonly ICompanyCode[]>([]);
    private readonly loadingCompanyCodeIds = new Set<string>();
    public readonly selectedCompanyCodeId = signal<string | null>(null);
    public readonly companyCodeOptionsLoading = signal(false);
    public readonly companyCodeUpdating = signal(false);
    public readonly companyCodeLoadFailed = signal(false);

    public readonly isEditingInvoice = computed(() => !!this.existingInvoiceId() || !!this.taskId());

    public readonly isReceiptBasedInvoice = computed(() => {
        const invoice = this.invoice();
        return this._store.receiptIds().length > 0 || !!invoice?.hasReceiptRelation;
    });
    public readonly receiptBasedAccountingFieldsReadOnly = computed(() => {
        if (!this.isReceiptBasedInvoice()) {
            return false;
        }
        return !(this.invoiceSettings()?.receiptBasedAccountingEditable ?? true);
    });
    public readonly receiptBasedPriceFieldsReadOnly = computed(() => {
        if (!this.isReceiptBasedInvoice()) {
            return false;
        }
        return !(this.invoiceSettings()?.receiptBasedPriceEditable ?? true);
    });
    public readonly showReceiptEditingRestrictionNotice = computed(() => {
        return this.receiptBasedAccountingFieldsReadOnly() || this.receiptBasedPriceFieldsReadOnly();
    });

    public readonly canSelectCompanyCode = computed(() => {
        const invoice = this.invoice();
        if (!invoice) {
            return false;
        }
        if (this.companyCodes().length <= 1) {
            return false;
        }
        return !this.isEditingInvoice() && !this.isReceiptBasedInvoice();
    });

    public readonly showCompanyCodeCard = computed(() => !!this.invoice());

    public readonly showCompanyCodeSelection = computed(() => this.canSelectCompanyCode());

    public readonly requiresCompanyCodeSelection = computed(() => {
        const invoice = this.invoice();
        if (!invoice) {
            return false;
        }
        return !invoice.companyCodeId && !this.selectedCompanyCodeId();
    });

    public readonly companyCodeIdForAccounting = computed(() => {
        const invoice = this.invoice();
        if (invoice?.hasReceiptRelation) {
            return invoice.companyCodeId ?? '';
        }
        return this.selectedCompanyCodeId() ?? invoice?.companyCodeId ?? '';
    });

    public readonly companyCodeOptions = computed(() => {
        return this.companyCodes().map((companyCode) => ({
            id: companyCode.id,
            label: this.formatCompanyCodeLabel(companyCode),
        }));
    });

    public readonly sortedCompanyCodeOptions = computed(() =>
        [...this.companyCodeOptions()].sort((left, right) => left.label.localeCompare(right.label)),
    );

    public readonly selectedCompanyCodeLabel = computed(() => {
        const companyCodeId = this.selectedCompanyCodeId() ?? this.invoice()?.companyCodeId ?? null;
        if (!companyCodeId) {
            return '-';
        }
        const matching =
            this.companyCodes().find((code) => code.id === companyCodeId) ??
            this.companyCodeOptionsCache().find((code) => code.id === companyCodeId);
        if (matching) {
            return this.formatCompanyCodeLabel(matching);
        }
        return companyCodeId;
    });

    // Cache for delivery receipt supplier information
    private readonly _supplierCache = signal<Map<number, string>>(new Map());

    // Form group for the invoice
    public form!: FormGroup<ICreateStandaloneInvoiceForm>;

    public isMobile: Signal<boolean>;
    public files: WritableSignal<FormDataEntryValue[]> = signal([]);

    // Accounting related properties
    public accountingInputs: AccountingSelectionInput[] = [];
    public accountingChangeData: Map<number, AccountingChange[]> = new Map<number, AccountingChange[]>();
    public accountingValidMap: Map<number, boolean> = new Map();
    public materialGroupValidMap: Map<number, boolean> = new Map();
    public recommendedMaterialGroupCodesByIndex: Array<readonly string[] | null> = [];
    public recommendedMaterialGroupSourceLabelsByIndex: Array<string | null> = [];
    public recommendedMaterialGroupReasonLabelsByIndex: Array<string | null> = [];
    private readonly pendingGeneralLedgerAutofill = new Set<number>();
    private readonly pendingMaterialGroupAutofill = new Set<number>();

    public maxDate: Dayjs;

    // Accounting related events
    @Output() public accountingValidationChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() public accountingInitialized = new EventEmitter<void>();

    public readonly hasPdfFileAttachments: Signal<boolean> = computed(() => {
        const files = this.files();
        const isMobile = this.isMobile();

        const hasPdf = files.some((file) => {
            if (file instanceof File) {
                return isPdf(file.type) || file.name?.toLowerCase().endsWith('.pdf');
            }
            return false;
        });

        return hasPdf && !isMobile;
    });

    public readonly showPdfPreview: Signal<boolean> = computed(() => {
        const hasPdf = this.hasPdfFileAttachments();
        const isActive = this.displayPdfPreview();
        const show = hasPdf && isActive;
        return show;
    });

    public readonly showIncompleteWarning: Signal<boolean> = computed(() => {
        if (!this.allowSavingWithMissingFields()) {
            return false;
        }

        return this._hasUnmetRequiredFields();
    });

    private readonly _formValueVersion = signal(0);

    public readonly selectedCurrencyCode: Signal<string> = computed(() => {
        this._formValueVersion();
        return this.form?.controls.head.controls.currency.value?.value ?? 'EUR';
    });

    // Head-derived values used repeatedly in item/charge templates to minimize dependencies
    public readonly selectedCurrencyDisplay: Signal<string | null> = computed(() => {
        this._formValueVersion();
        return this.form?.controls.head.controls.currency.value?.valueDisplay ?? this.selectedCurrencyCode();
    });

    public readonly itemLineTotalsFromDraft: Signal<readonly (IMoney | null)[]> = computed(() => {
        this._formValueVersion();
        const invoice = this.invoice();
        if (!this.form || !invoice) {
            return [];
        }

        const lineTotalByItemId = new Map<string, IMoney | null>();
        invoice.items.forEach((item) => {
            lineTotalByItemId.set(item.id, item.lineTotal ?? null);
        });

        return this.form.controls.items.controls.map((itemControl) => {
            const itemId = itemControl.controls.id.value;
            if (!itemId) {
                return null;
            }
            return lineTotalByItemId.get(itemId) ?? null;
        });
    });

    public readonly shouldUseFrontendPricingCalculation: Signal<boolean> = computed(() => {
        this._formValueVersion();
        const invoice = this.invoice();
        if (!this.form || !invoice) {
            return false;
        }

        if (this.form.controls.items.length !== invoice.items.length) {
            return true;
        }

        if (this.form.controls.charges.length !== invoice.charges.length) {
            return true;
        }

        const hasItemPricingChanges = this.form.controls.items.controls.some((itemControl) => {
            return (
                itemControl.controls.quantity.dirty ||
                itemControl.controls.unitPrice.dirty ||
                itemControl.controls.priceQuantity.dirty ||
                itemControl.controls.discountPercentage.dirty ||
                itemControl.controls.taxInPercent.dirty
            );
        });
        if (hasItemPricingChanges) {
            return true;
        }

        const hasChargePricingChanges = this.form.controls.charges.controls.some((chargeControl) => {
            return chargeControl.controls.price.dirty || chargeControl.controls.taxInPercent.dirty;
        });

        return hasChargePricingChanges;
    });

    public readonly itemLineTotalsCalculated: Signal<readonly IMoney[]> = computed(() => {
        this._formValueVersion();
        if (!this.form) {
            return [];
        }

        const currency = this.selectedCurrencyCode();
        return this.form.controls.items.controls.map((itemControl) => ({
            amount: this.computeItemNetTotal(itemControl),
            currency,
        }));
    });

    public readonly displayedItemLineTotals: Signal<readonly (IMoney | null)[]> = computed(() => {
        const calculatedLineTotals = this.itemLineTotalsCalculated();
        if (this.shouldUseFrontendPricingCalculation()) {
            return calculatedLineTotals;
        }

        const draftLineTotals = this.itemLineTotalsFromDraft();
        if (draftLineTotals.length === 0) {
            return calculatedLineTotals;
        }

        return draftLineTotals.map((draftLineTotal, index) => draftLineTotal ?? calculatedLineTotals[index] ?? null);
    });

    public readonly invoiceTotalsFromDraft: Signal<IInvoiceTotals | null> = computed(() => {
        const draftPrice = this.invoice()?.price;
        if (!draftPrice) {
            return null;
        }

        return {
            netAmount: draftPrice.totalNetPrice,
            taxes: (draftPrice.taxes ?? []).map((tax) => ({
                percent: tax.percent,
                amount: tax.amount,
            })),
            grossAmount: draftPrice.totalGrossPrice,
            currency: draftPrice.currency,
        };
    });

    public readonly invoiceTotalsCalculated: Signal<IInvoiceTotals> = computed(() => {
        this._formValueVersion();

        const currency = this.selectedCurrencyCode();
        if (!this.form) {
            return {
                netAmount: 0,
                taxes: [],
                grossAmount: 0,
                currency,
            };
        }

        const taxAmountByPercent = new Map<number, number>();
        let netAmount = 0;

        this.form.controls.items.controls.forEach((itemControl) => {
            const itemNetAmount = this.computeItemNetTotal(itemControl);
            netAmount = this.roundMoney(netAmount + itemNetAmount);

            const taxPercent = this.normalizeFiniteNumber(itemControl.controls.taxInPercent.value);
            const itemTaxAmount = this.computeTaxAmount(itemNetAmount, taxPercent);
            this.updateTaxSummary(taxAmountByPercent, taxPercent, itemTaxAmount);
        });

        this.form.controls.charges.controls.forEach((chargeControl) => {
            const chargeNetAmount = this.computeChargeNetTotal(chargeControl);
            netAmount = this.roundMoney(netAmount + chargeNetAmount);

            const taxPercent = this.normalizeFiniteNumber(chargeControl.controls.taxInPercent.value);
            const chargeTaxAmount = this.computeTaxAmount(chargeNetAmount, taxPercent);
            this.updateTaxSummary(taxAmountByPercent, taxPercent, chargeTaxAmount);
        });

        const taxes: readonly IInvoiceTaxSummary[] = Array.from(taxAmountByPercent.entries())
            .sort(([firstPercent], [secondPercent]) => firstPercent - secondPercent)
            .map(([percent, amount]) => ({
                percent,
                amount,
            }));

        const totalTaxAmount = taxes.reduce((acc, tax) => this.roundMoney(acc + tax.amount), 0);

        return {
            netAmount,
            taxes,
            grossAmount: this.roundMoney(netAmount + totalTaxAmount),
            currency,
        };
    });

    public readonly displayedInvoiceTotals: Signal<IInvoiceTotals> = computed(() => {
        const draftTotals = this.invoiceTotalsFromDraft();
        if (this.shouldUseFrontendPricingCalculation() || !draftTotals) {
            return this.invoiceTotalsCalculated();
        }

        return draftTotals;
    });

    public readonly supplierSelected: Signal<boolean> = computed(() => !!this.invoice()?.supplier?.id);

    public readonly heading: Signal<string> = computed(() => {
        const invoiceId = this.existingInvoiceId();
        const taskId = this.taskId();

        if (taskId || invoiceId) {
            return marker('Rechnung bearbeiten');
        }

        return marker('Rechnung erstellen');
    });

    public router = inject(Router);
    // Mobile state observable
    private readonly _store = inject(CreateOrUpdateStandaloneInvoiceStore);
    private readonly _breadcrumbService = inject(BreadcrumbService);
    private readonly _formBuilder = inject(FormBuilder);
    private readonly _supplierService = inject(SupplierService);
    private readonly _supplierSearchService = inject(SupplierSearchService);
    private readonly _departmentService = inject(DepartmentService);
    private readonly _addressService = inject(AddressService);
    private readonly _invoiceAddressDataService = inject(InvoiceAddressDataService);
    private readonly _bottomSheetService = inject(BottomSheetService);
    private readonly _mobileService = inject(MobileService);
    private readonly _destroyRef = inject(DestroyRef);
    private readonly _route = inject(ActivatedRoute);
    private readonly _dialog = inject(MatDialog);
    private readonly _dialogRef = inject(MatDialogRef<CreateOrUpdateStandaloneInvoiceComponent>, { optional: true });
    private readonly _dialogData = inject<InvoiceEditDialogData | null>(MAT_DIALOG_DATA, { optional: true });
    private readonly _deliveryReceiptDataService = inject(DeliveryReceiptDataService);
    private readonly _orderDataService = inject(OrderDataService);
    private readonly _confirmationService = inject(ConfirmationService);
    private readonly _accountSelection = inject(AccountingSelectionService);
    private readonly _changeDetectorRef = inject(ChangeDetectorRef);
    private readonly _companyCodeDataService = inject(CompanyCodeDataService);
    private readonly _notificationService = inject(NotificationService);
    public readonly invoiceNumberValidator = inject(InvoiceNumberValidatorService);
    protected readonly InvoiceNumberValidatorService = InvoiceNumberValidatorService;
    protected readonly TextModuleCategoryEnum = TextModuleCategoryEnum;

    public readonly displayPdfPreview = signal(true);
    private readonly _hasUnmetRequiredFields = signal(false);
    private readonly isInitialLoad = signal(true);

    public invoiceNumberErrorMessages: CustomErrorMessages = {
        [InvoiceNumberValidatorService.invoiceNumberExistsErrorName]: marker(
            'Die Rechnungsnummer existiert bereits für den Lieferanten.',
        ),
        [InvoiceNumberValidatorService.invoiceNumberInvalidFormatErrorName]: marker(
            'Die Rechnungsnummer hat nicht das richtige Format.',
        ),
        [InvoiceNumberValidatorService.invoiceNumberInvalid]: marker('Die Rechnungsnummer ist ungültig.'),
    };

    public readonly incompleteSaveWarningText = marker(
        'Pflichtfelder fehlen. Im Freigabeprozess kann trotzdem gespeichert werden, eine Freigabe ist jedoch nur mit einer vollständigen Rechnung möglich.',
    );

    public get insideDialog(): boolean {
        return !!this._dialogRef;
    }

    /**
     * Creates a validator function that checks if attachments are mandatory and if file types are restricted
     * @param settings The invoice attachment settings
     * @returns A validator function
     */
    private createAttachmentValidator(settings: InvoiceSettingsDto | null): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            // If no settings or attachments are not mandatory, return null (valid)
            if (!settings || !settings.attachment.attachmentsMandatory) {
                return null;
            }

            const filesControl = control.get('files') as FormControl<FormData>;
            const scannedImagesControl = control.get('scannedImages') as FormControl<FormData>;

            // Check if either files or scannedImages has at least one element
            const filesFormData = filesControl.value;
            const scannedImagesFormData = scannedImagesControl.value;

            // Check if FormData has entries
            let hasFiles = false;
            filesFormData.forEach(() => {
                hasFiles = true;
            });

            let hasScannedImages = false;
            scannedImagesFormData.forEach(() => {
                hasScannedImages = true;
            });

            // If attachments are mandatory and neither files nor scannedImages has elements, return error
            if (settings.attachment.attachmentsMandatory && !hasFiles && !hasScannedImages) {
                return { attachmentRequired: true };
            }

            // Check file types if restricted
            if (settings.attachment.restrictToAcceptedMimeTypes && hasFiles) {
                const acceptedMimeTypes = settings.attachment.acceptedMimeTypes;
                let hasInvalidFileType = false;

                filesFormData.forEach((file: File) => {
                    if (!acceptedMimeTypes.includes(file.type)) {
                        hasInvalidFileType = true;
                    }
                });

                if (hasInvalidFileType) {
                    return { invalidFileType: true };
                }
            }

            return null;
        };
    }

    private updateMaterialGroupValidators(): void {
        if (!this.form) {
            return;
        }

        const validators = this.isMaterialGroupRequired() ? [Validators.required] : [];
        this.form.controls.items.controls.forEach((itemGroup) => {
            itemGroup.controls.materialGroupCode.setValidators(validators);
            itemGroup.controls.materialGroupCode.updateValueAndValidity({ emitEvent: false });
        });
    }

    private syncReceiptBasedEditability(): void {
        if (!this.form) {
            return;
        }

        const disableReceiptBasedPriceFields = this.receiptBasedPriceFieldsReadOnly();

        this.form.controls.items.controls.forEach((itemGroup) => {
            const isReceiptLinked = !!itemGroup.controls.deliveryRelation.value;
            const disableItemPriceControls = disableReceiptBasedPriceFields && isReceiptLinked;

            this.setControlDisabledState(itemGroup.controls.unitPrice, disableItemPriceControls);
            this.setControlDisabledState(itemGroup.controls.discountPercentage, disableItemPriceControls);
            this.setControlDisabledState(itemGroup.controls.taxInPercent, disableItemPriceControls);
        });

        this.form.controls.charges.controls.forEach((chargeGroup) => {
            Object.values(chargeGroup.controls).forEach((control) => {
                this.setControlDisabledState(control, disableReceiptBasedPriceFields);
            });
        });
    }

    private setControlDisabledState(control: AbstractControl, disabled: boolean): void {
        if (disabled && control.enabled) {
            control.disable({ emitEvent: false });
            return;
        }

        if (!disabled && control.disabled) {
            control.enable({ emitEvent: false });
        }
    }

    constructor() {
        this.initForm();
        this.isMobile = computed(() => this._mobileService.isMobile().mobile);

        this.maxDate = dayjs();

        effect(() => {
            if (this.hasPdfFileAttachments()) {
                this.displayPdfPreview.set(true);
            }
        });

        effect(() => {
            const invoice = this.invoice();
            if (invoice) {
                if (this.isInitialLoad() && !this.form.touched) {
                    // Only apply invoice data to form during initial load
                    this.applyInvoiceToForm(invoice);
                    void this.populateFormDataWithInvoiceFiles(invoice);

                    this.isInitialLoad.set(false);
                }
            }

            // Update the invoice number validator with the supplier company ID
            if (invoice?.supplier?.id && this.form) {
                const invoiceNumberControl = this.form.controls.head.controls.invoiceNumber;
                const invoiceId = this._store.invoiceId();
                invoiceNumberControl.setAsyncValidators(
                    this.invoiceNumberValidator.get(invoice.supplier.id, invoiceId),
                );
                // Only trigger validation if the control has a value
                if (invoiceNumberControl.value) {
                    invoiceNumberControl.updateValueAndValidity();
                }
            }
        });

        // Apply validator when invoice settings change
        effect(() => {
            const settings = this.invoiceSettings();
            if (settings) {
                const validator = this.createAttachmentValidator(settings);
                this.form.controls.head.setValidators(validator);
                this.form.controls.head.updateValueAndValidity();
                this.updateMaterialGroupValidators();
            }
        });

        effect(() => {
            this._formValueVersion();
            this.receiptBasedPriceFieldsReadOnly();
            this.syncReceiptBasedEditability();
        });

        effect(() => {
            const invoice = this.invoice();
            const codes = this.companyCodes();
            if (!invoice) {
                return;
            }
            if (invoice.companyCodeId && this.selectedCompanyCodeId() !== invoice.companyCodeId) {
                this.selectedCompanyCodeId.set(invoice.companyCodeId);
                return;
            }
            if (!invoice.companyCodeId && !this.selectedCompanyCodeId() && codes.length === 1) {
                void this.applyCompanyCode(codes[0].id);
            }
        });

        effect(() => {
            const companyCodeId = this.selectedCompanyCodeId() ?? this.invoice()?.companyCodeId ?? null;
            if (!companyCodeId) {
                return;
            }

            const isKnownCompanyCode =
                this.companyCodes().some((code) => code.id === companyCodeId) ||
                this.companyCodeOptionsCache().some((code) => code.id === companyCodeId);
            if (isKnownCompanyCode) {
                return;
            }

            this.loadCompanyCodeById(companyCodeId);
        });

        this.form.statusChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
            this.recomputeRequiredFieldState();
        });
        this.form.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
            this._formValueVersion.update((currentVersion) => currentVersion + 1);
            this.recomputeRequiredFieldState();
        });

        effect(() => {
            this.invoice();
            this.invoiceSettings();
            this.recomputeRequiredFieldState();
        });
    }

    get isDepartmentRequired(): boolean {
        return this.invoiceSettings()?.departmentRequired ?? false;
    }

    get showDepartmentRequiredIndicator(): boolean {
        return this.isDepartmentRequired && !this.invoice()?.department?.name;
    }

    togglePdfPreview(): void {
        this.displayPdfPreview.set(!this.displayPdfPreview());
        this._changeDetectorRef.markForCheck();
    }

    private formatIsoDateTimeOrNull(control: FormControl<string>): string | null {
        const parsedDate = dayjs(control.value);
        return parsedDate.isValid() ? parsedDate.format(CreateOrUpdateStandaloneInvoiceComponent.ISO_DATE_TIME) : null;
    }

    private resolveCompanyCodeId(): string | null {
        return this.selectedCompanyCodeId() ?? this.invoice()?.companyCodeId ?? null;
    }

    ngOnInit(): void {
        // Detect dialog data first
        const dialogData = this._dialogData;
        if (dialogData?.taskId) {
            this._store.setTaskId(dialogData.taskId);
        }
        if (dialogData?.invoiceId) {
            this._store.loadInitialDataWithInvoiceId(dialogData.invoiceId);
        } else {
            // Load initial data when not in dialog, using route params
            let receiptIds: string[] | string = this._route.snapshot.queryParams['receiptId'] ?? [];
            if (!Array.isArray(receiptIds)) {
                receiptIds = [receiptIds];
            }

            const invoiceId = this._route.snapshot.queryParams['invoiceId'];

            if (invoiceId) {
                this._store.loadInitialDataWithInvoiceId(invoiceId);
            } else {
                this._store.loadInitialData(!!receiptIds ? receiptIds.map((id) => +id) : []);
            }
        }

        // Set breadcrumbs depending on origin
        // If a delivery receipt id is present in query params, show delivery receipt detail breadcrumbs + invoice create
        const qpReceiptIds: string[] | string = this._route.snapshot.queryParams['receiptId'] ?? [];
        const receiptIdList: string[] = Array.isArray(qpReceiptIds)
            ? qpReceiptIds.filter((id) => id !== undefined && id !== '')
            : qpReceiptIds !== undefined && qpReceiptIds !== ''
              ? [qpReceiptIds]
              : [];
        const hasReceiptRef = receiptIdList.length > 0;

        if (!this.insideDialog) {
            if (hasReceiptRef) {
                if (receiptIdList.length === 1) {
                    // Use the single receipt id for breadcrumb context
                    const firstReceiptId = +receiptIdList[0];
                    if (!isNaN(firstReceiptId)) {
                        const receiptNumber: string = this._route.snapshot.queryParams['receiptNumber'] ?? '';
                        this._breadcrumbService.set(
                            BC.MY_ADMIN.LIST,
                            BC.MY_ADMIN.DELIVERY.LIST,
                            BC.MY_ADMIN.DELIVERY.DETAIL(firstReceiptId, receiptNumber),
                            BC.MY_ADMIN.INVOICE.CREATE_STANDALONE,
                        );
                    } else {
                        // Invalid id fallback
                        this._breadcrumbService.set(
                            BC.MY_ADMIN.LIST,
                            BC.MY_ADMIN.INVOICE.LIST,
                            BC.MY_ADMIN.INVOICE.CREATE_STANDALONE,
                        );
                    }
                } else {
                    // Multiple receipts selected: omit specific delivery receipt breadcrumb
                    this._breadcrumbService.set(
                        BC.MY_ADMIN.LIST,
                        BC.MY_ADMIN.DELIVERY.LIST,
                        BC.MY_ADMIN.INVOICE.CREATE_STANDALONE,
                    );
                }
            } else {
                // Default breadcrumbs
                this._breadcrumbService.set(
                    BC.MY_ADMIN.LIST,
                    BC.MY_ADMIN.INVOICE.LIST,
                    BC.MY_ADMIN.INVOICE.CREATE_STANDALONE,
                );
            }
        }

        void this.loadCompanyCodes();
    }

    public async handleCompanyCodeSelection(companyCodeId: string | null): Promise<void> {
        if (!this.canSelectCompanyCode()) {
            return;
        }
        if (!companyCodeId) {
            return;
        }
        if (companyCodeId === this.selectedCompanyCodeId()) {
            return;
        }
        await this.applyCompanyCode(companyCodeId);
    }

    private loadCompanyCodes(): void {
        this.companyCodeOptionsLoading.set(true);
        this.companyCodeLoadFailed.set(false);
        this._companyCodeDataService
            .getMyActive()
            .pipe(
                finalize(() => {
                    this.companyCodeOptionsLoading.set(false);
                }),
            )
            .subscribe({
                next: (codes) => {
                    this.companyCodes.set(codes ?? []);
                    this.companyCodeOptionsCache.set(codes ?? []);
                    const invoice = this.invoice();
                    if (invoice?.companyCodeId) {
                        this.selectedCompanyCodeId.set(invoice.companyCodeId);
                    } else if (codes.length === 1) {
                        void this.applyCompanyCode(codes[0].id);
                    }
                },
                error: () => {
                    this.companyCodes.set([]);
                    this.companyCodeOptionsCache.set([]);
                    this.companyCodeLoadFailed.set(true);
                },
            });
    }

    private async applyCompanyCode(companyCodeId: string): Promise<void> {
        const invoice = this.invoice();
        if (!invoice) {
            return;
        }
        if (invoice.companyCodeId === companyCodeId && this.selectedCompanyCodeId() === companyCodeId) {
            return;
        }
        this.companyCodeUpdating.set(true);
        try {
            this._store.updateCompanyCode(companyCodeId);
            this.selectedCompanyCodeId.set(companyCodeId);
            await this.ensureInvoiceAddressMatchesCompanyCode(companyCodeId);
            await this.ensureSupplierAvailableForCompanyCode(companyCodeId);
        } finally {
            this.companyCodeUpdating.set(false);
        }
    }

    private formatCompanyCodeLabel(companyCode: ICompanyCode): string {
        if (!companyCode.name) {
            return companyCode.code;
        }
        return `${companyCode.code} (${companyCode.name})`;
    }

    private loadCompanyCodeById(companyCodeId: string): void {
        if (this.loadingCompanyCodeIds.has(companyCodeId)) {
            return;
        }

        this.loadingCompanyCodeIds.add(companyCodeId);
        this._companyCodeDataService
            .getById(companyCodeId)
            .pipe(
                take(1),
                finalize(() => this.loadingCompanyCodeIds.delete(companyCodeId)),
            )
            .subscribe({
                next: (companyCode) => {
                    this.companyCodeOptionsCache.update((currentCodes) => {
                        if (currentCodes.some((code) => code.id === companyCode.id)) {
                            return currentCodes;
                        }
                        return [...currentCodes, companyCode];
                    });
                },
                error: () => {
                    // keep UUID fallback only if lookup fails
                },
            });
    }

    changeAddress(): void {
        const currentInvoice = this.invoice();
        if (currentInvoice) {
            const companyCodeId = this.resolveCompanyCodeId();
            if (!companyCodeId) {
                return;
            }
            this._addressService
                .selectAddress(AddressType.INVOICE, undefined, companyCodeId)
                .pipe(take(1))
                .subscribe((selectedAddress) => {
                    if (selectedAddress) {
                        // Update the invoice in the store with the new address
                        this._store.updateAddress(selectedAddress.companyAddressWithContact);
                    }
                });
        }
    }

    private async ensureInvoiceAddressMatchesCompanyCode(companyCodeId: string): Promise<void> {
        const currentInvoice = this.invoice();
        const currentAddressId = currentInvoice?.invoiceAddress?.id;
        if (!currentAddressId) {
            return;
        }

        try {
            const response = await firstValueFrom(this._invoiceAddressDataService.getAllUnlockedForUser(companyCodeId));
            const isStillValid = response.invoiceAddresses.some((address) => address.id === currentAddressId);
            if (isStillValid) {
                return;
            }

            const fallbackAddress = this.resolveFallbackInvoiceAddress(response.invoiceAddresses, response.defaultAddressId);
            if (fallbackAddress) {
                this._store.updateAddress(CompanyAddressWithContact.fromCustomerAddressDto(fallbackAddress));
            } else {
                this._store.clearAddress();
            }
        } catch {
            this._store.clearAddress();
        }
    }

    private async ensureSupplierAvailableForCompanyCode(companyCodeId: string): Promise<void> {
        const currentInvoice = this.invoice();
        const currentSupplierId = currentInvoice?.supplier?.id;
        if (!currentInvoice || !currentSupplierId) {
            return;
        }
        const supplierNameQuery = currentInvoice.supplier.name ?? '';
        try {
            const supplierAvailable = await this.isSupplierAvailableForCompanyCode(
                currentSupplierId,
                companyCodeId,
                supplierNameQuery,
            );
            if (supplierAvailable) {
                return;
            }
            this._store.clearSupplier();
            this._clearAllDeliveryReceiptRelations();
            const infoMessage = marker(
                'Der ausgewählte Lieferant ist für diesen Buchungskreis nicht verfügbar und wurde entfernt.',
            );
            this._notificationService.showNotification(infoMessage, NotificationMessageLevel.INFO);
        } catch {
            // keep supplier if availability check failed
        }
    }

    private async isSupplierAvailableForCompanyCode(
        supplierId: string,
        companyCodeId: string,
        supplierNameQuery: string,
    ): Promise<boolean> {
        const queries = supplierNameQuery ? [supplierNameQuery, ''] : [''];
        for (const query of queries) {
            const suppliers = await firstValueFrom(this._supplierSearchService.search(query, false, companyCodeId));
            if (suppliers.some((supplier) => supplier.id === supplierId)) {
                return true;
            }
        }
        return false;
    }

    private resolveFallbackInvoiceAddress(
        addresses: readonly ICustomerAddressDto[],
        defaultAddressId?: number,
    ): ICustomerAddressDto | undefined {
        if (defaultAddressId != null) {
            const defaultAddress = addresses.find((address) => address.id === defaultAddressId);
            if (defaultAddress) {
                return defaultAddress;
            }
        }
        return addresses[0];
    }

    isSaveButtonDisabled(): boolean {
        if (this.allowSavingWithMissingFields()) {
            return (
                this.isLoading() ||
                !this.invoice() ||
                this.requiresCompanyCodeSelection() ||
                this.hasBlockingItemValidationErrors()
            );
        }

        return this.formInvalidForStandardSave();
    }

    cancel() {
        this.router.navigate([View.CLIENT, 'my-admin', 'invoice', 'list']);
    }

    private formInvalidForStandardSave(): boolean {
        return (
            this.form.invalid ||
            this.hasInvalidAccounting() ||
            this.hasInvalidMaterialGroups() ||
            this.isInvoiceAddressMissing() ||
            this.isRequiredDepartmentMissing() ||
            this.requiresCompanyCodeSelection()
        );
    }

    private hasUnmetRequiredFields(): boolean {
        return (
            this.form.invalid ||
            this.isInvoiceAddressMissing() ||
            this.isRequiredDepartmentMissing() ||
            this.hasInvalidAccounting() ||
            this.hasInvalidMaterialGroups() ||
            this.requiresCompanyCodeSelection()
        );
    }

    private recomputeRequiredFieldState(): void {
        if (!this.form) {
            return;
        }
        this._hasUnmetRequiredFields.set(this.hasUnmetRequiredFields());
    }

    private computeItemNetTotal(itemControl: FormGroup<ICreateStandaloneInvoiceItemForm>): number {
        const quantity = this.normalizeFiniteNumber(itemControl.controls.quantity.value);
        const unitPrice = this.normalizeFiniteNumber(itemControl.controls.unitPrice.value);
        const discountPercentage = this.normalizeFiniteNumber(itemControl.controls.discountPercentage.value);
        const rawPriceQuantity = this.normalizeFiniteNumber(itemControl.controls.priceQuantity.value);
        const priceQuantity = rawPriceQuantity > 0 ? rawPriceQuantity : 1;

        const discountFactor = Math.max(0, 1 - discountPercentage / 100);
        const itemNetTotal = (quantity / priceQuantity) * unitPrice * discountFactor;
        return this.roundMoney(itemNetTotal);
    }

    private computeChargeNetTotal(chargeControl: FormGroup<ICreateInvoiceChargesForm>): number {
        return this.roundMoney(this.normalizeFiniteNumber(chargeControl.controls.price.value));
    }

    private computeTaxAmount(netAmount: number, taxPercent: number): number {
        const normalizedTaxPercent = this.normalizeFiniteNumber(taxPercent);
        return this.roundMoney((netAmount * normalizedTaxPercent) / 100);
    }

    private updateTaxSummary(taxAmountByPercent: Map<number, number>, taxPercent: number, taxAmount: number): void {
        const currentTaxAmount = taxAmountByPercent.get(taxPercent) ?? 0;
        taxAmountByPercent.set(taxPercent, this.roundMoney(currentTaxAmount + taxAmount));
    }

    private normalizeFiniteNumber(value: number): number {
        return Number.isFinite(value) ? value : 0;
    }

    private roundMoney(value: number): number {
        return Math.round((value + Number.EPSILON) * 100) / 100;
    }


    private hasInvalidAccounting(): boolean {
        return Array.from(this.accountingValidMap.values()).some((v) => !v);
    }

    private isInvoiceAddressMissing(): boolean {
        const currentInvoice = this.invoice();
        return !currentInvoice || !currentInvoice.invoiceAddress;
    }

    private isRequiredDepartmentMissing(): boolean {
        return this.invoiceSettings()?.departmentRequired && !this.invoice()?.department?.index;
    }

    private isMaterialGroupRequired(): boolean {
        return this.invoiceSettings()?.materialGroupRequired ?? false;
    }

    private hasInvalidMaterialGroups(): boolean {
        if (Array.from(this.materialGroupValidMap.values()).some((value) => !value)) {
            return true;
        }

        if (!this.isMaterialGroupRequired()) {
            return false;
        }

        return this.form.controls.items.controls.some((item) => !item.controls.materialGroupCode.value);
    }

    private hasBlockingItemValidationErrors(): boolean {
        return this.hasInvalidAccounting() || this.hasInvalidMaterialGroups();
    }

    /**
     * Creates a validator function that checks if the items array has at least one item
     */
    private createItemsRequiredValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const itemsArray = control.get('items') as FormArray;
            if (!itemsArray || itemsArray.length === 0) {
                return { itemsRequired: true };
            }
            return null;
        };
    }

    /**
     * Initialize the form with default values
     */
    private initForm(): void {
        this.form = this._formBuilder.group(
            {
                head: this._formBuilder.group({
                    invoiceNumber: new FormControl<string>('', {
                        nonNullable: true,
                        validators: [Validators.required],
                    }),
                    documentDate: new FormControl<string>('', {
                        nonNullable: true,
                        validators: [Validators.required],
                    }),
                    paymentDate: new FormControl<string>('', {
                        nonNullable: true,
                    }),
                    paymentUntil: new FormControl<string>('', {
                        nonNullable: true,
                        validators: [Validators.required],
                    }),
                    currency: new FormControl<IimFilterableRecord<string>>(new IimFilterableRecord<string>('EUR'), {
                        nonNullable: true,
                    }),
                    remark: new FormControl<string>('', {
                        nonNullable: true,
                    }),
                    files: new FormControl<FormData>(new FormData(), {
                        nonNullable: true,
                    }),
                    scannedImages: new FormControl<FormData>(new FormData(), {
                        nonNullable: true,
                    }),
                }),
                items: this._formBuilder.array<FormGroup<ICreateStandaloneInvoiceItemForm>>([]),
                charges: this._formBuilder.array<FormGroup<ICreateInvoiceChargesForm>>([]),
                comment: new FormControl('', { nonNullable: true }),
            },
            { validators: this.createItemsRequiredValidator() },
        );
        this.form.controls.head.controls.files.valueChanges
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe((files: FormData) => {
                this.files.set(files.getAll('files'));
            });

        // Emit invoiceChanged on general form edits while inside dialog
        this.form.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
            if (this.insideDialog) {
                this.invoiceChanged.emit();
            }
        });

        // Sync additional cost currencies with main invoice currency
        this.form.controls.head.controls.currency.valueChanges
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe((mainCurrency: IimFilterableRecord<string>) => {
                if (mainCurrency) {
                    // Update all existing additional cost currencies to match the main currency
                    const charges = this.form.controls.charges;
                    charges.controls.forEach((chargeGroup) => {
                        chargeGroup.controls.currency.setValue(mainCurrency, { emitEvent: false });
                    });
                }
            });

    }

    /**
     * Apply invoice data from the store to the form
     */
    private applyInvoiceToForm(invoice: StandaloneInvoiceDraft): void {
        this.form.controls.head.patchValue({
            invoiceNumber: invoice.invoiceNumber,
            documentDate: invoice.documentDate,
            paymentDate: invoice.paymentDate,
            paymentUntil: invoice.paymentUntil,
            currency: new IimFilterableRecord<string>(invoice.currency || 'EUR'),
            remark: invoice.remark,
        });

        // Handle items
        const items: FormArray<FormGroup<ICreateStandaloneInvoiceItemForm>> = this.form.controls.items;
        items.clear();

        // Clear accounting inputs
        this.accountingInputs = [];
        this.accountingChangeData = new Map<number, AccountingChange[]>();
        this.accountingValidMap = new Map();
        this.materialGroupValidMap = new Map();
        this.recommendedMaterialGroupCodesByIndex = [];
        this.recommendedMaterialGroupSourceLabelsByIndex = [];
        this.recommendedMaterialGroupReasonLabelsByIndex = [];
        this.pendingGeneralLedgerAutofill.clear();
        this.pendingMaterialGroupAutofill.clear();

        if (invoice.items && invoice.items.length > 0) {
            invoice.items?.forEach((item, index) => {
                const deliveryRelationCtrl = new FormControl(item.deliveryRelation ?? undefined);
                // The manual order link travels with the save; without it the relation is left on an
                // item id the save replaces and is deleted with it.
                const orderRelationCtrl = new FormControl(item.orderRelation ?? undefined);
                const formGroup = this._formBuilder.group<ICreateStandaloneInvoiceItemForm>({
                    id: new FormControl(item.id, { nonNullable: true }),
                    supplierArticleNumber: new FormControl(item.supplierArticleNumber, { nonNullable: true }),
                    name: new FormControl(item.name, { nonNullable: true, validators: [Validators.required] }),
                    description: new FormControl(item.description, { nonNullable: true }),
                    materialGroupCode: new FormControl(item.materialGroupCode ?? '', { nonNullable: true }),
                    productUnit: new FormControl(item.productUnit, {
                        nonNullable: true,
                        validators: [Validators.required],
                    }),
                    contentUnitsPerOrderUnit: new FormControl(item.contentUnitsPerOrderUnit || 1, {
                        nonNullable: true,
                        validators: [Validators.min(0)],
                    }),
                    contentUnit: new FormControl(item.contentUnit || '', {
                        nonNullable: true,
                    }),
                    unitPrice: new FormControl(item.unitPrice, { nonNullable: true }),
                    quantity: new FormControl(item.quantity, {
                        nonNullable: true,
                        validators: [Validators.required, Validators.min(0)],
                    }),
                    discountPercentage: new FormControl(item.discountPercentage || 0, {
                        nonNullable: true,
                        validators: [Validators.min(0), Validators.max(100)],
                    }),
                    taxInPercent: new FormControl(item.taxInPercent, {
                        nonNullable: true,
                        validators: [Validators.min(0)],
                    }),
                    priceQuantity: new FormControl(item.priceQuantity || 1, {
                        nonNullable: true,
                        validators: [Validators.min(1)],
                    }),
                    deliveryRelation: deliveryRelationCtrl,
                    orderRelation: orderRelationCtrl,
                });
                items.push(formGroup);

                // Initialize accounting input for this item
                this.initAccountingInput(index, item.accountingInput);
                // Seed as valid; the accounting component emits the real state once it has validated.
                this.accountingValidMap.set(index, true);
                for (const accountingChange of item.accountingChanges) {
                    this.emitAccountingChanged(accountingChange, index);
                }
                this.primeRecommendationAutofill(index);
            });
        }

        this.updateMaterialGroupValidators();

        // Build charges once (outside of the items loop)
        const chargesArray: FormArray<FormGroup<ICreateInvoiceChargesForm>> = this.form.controls.charges;
        chargesArray.clear();
        invoice.charges?.forEach((charge: CreateInvoiceCharge) => {
            const mainCurrency = new IimFilterableRecord<string>(invoice.currency || 'EUR');
            const chargeGroup = this._formBuilder.group<ICreateInvoiceChargesForm>({
                name: new FormControl<string>(charge.name, {
                    nonNullable: true,
                    validators: [Validators.required],
                }),
                description: new FormControl<string>(charge.description),
                articleNumberSupplier: new FormControl<string>(charge.articleNumberSupplier),
                articleNumberCustomer: new FormControl<string>(charge.articleNumberCustomer),
                price: new FormControl<number>(charge.price.amount),
                taxInPercent: new FormControl<number>(charge.taxInPercent),
                currency: new FormControl<IimFilterableRecord<string>>(mainCurrency),
                internalNote: new FormControl<string>(charge.internalNote),
                chargeType: new FormControl<string>(charge.chargeType),
            });
            chargesArray.push(chargeGroup);
        });

        this.form.controls.comment.patchValue(invoice.comment);

        // Update form validity after modifying items
        this.form.updateValueAndValidity();
    }

    private async populateFormDataWithInvoiceFiles(invoice: StandaloneInvoiceDraft): Promise<void> {
        const existingFiles = invoice.files ?? [];
        if (!existingFiles || existingFiles.length === 0) {
            return;
        }

        const current = this.form.controls.head.controls.files.value;
        const collectedFiles: File[] = [];

        for (const f of existingFiles) {
            const link: string | undefined = (f as any)?.fileLink;
            if (!link) continue;
            try {
                const resp = await fetch(link);
                const blob = await resp.blob();
                const type = (f as any)?.fileType || blob.type || 'application/octet-stream';
                const fileName = (f as any)?.fileName || 'file';
                const file = new File([blob], fileName, { type });
                current.append('files', file);
                collectedFiles.push(file);
            } catch {
                // ignore individual failures
            }
        }

        // Trigger valueChanges and update local signal
        this.form.controls.head.controls.files.setValue(current);
        if (collectedFiles.length > 0) {
            this.files.set(collectedFiles);
            // Automatically show PDF preview if PDF files were loaded
            const hasPdf = collectedFiles.some(
                (file) =>
                    file instanceof File &&
                    (file.type === 'application/pdf' || file.name?.toLowerCase().endsWith('.pdf')),
            );
            if (hasPdf && !this.isMobile()) {
                this.displayPdfPreview.set(true);
            }
        }
    }

    public openReceipt(receiptId: number): void {
        const data = new DeliveryReceiptDetailDialogData(receiptId);
        this._dialog.open(DeliveryReceiptDetailComponent, DialogOptions.LARGE(data));
    }

    public onDialogCancel(): void {
        if (this._dialogRef) {
            this._dialogRef.close({ updated: false, cancelled: true });
        }
    }

    public async onDialogUpdate(): Promise<void> {
        await this.onSaveDraft();
        if (this._dialogRef) {
            this._dialogRef.close({ updated: true, cancelled: false });
        }
    }

    /**
     * Whether an invoice item can be linked to an order item (UC_2020, EPROC-12024).
     *
     * The candidate search is addressed by the invoice, so the action appears on a stored invoice;
     * the link itself stays in the form until the next save.
     */
    public readonly canLinkOrder = computed(() => Boolean(this.existingInvoiceId()));

    /**
     * Opens the manual order-linking dialog for one item and keeps the choice in the item form (UC_2020).
     *
     * Works like linking a delivery receipt: the dialog searches by the supplier and company code on
     * screen - also one picked a moment ago - and the choice travels with the next save. Nothing is
     * written before, so the invoice never has to be saved first, which the form may not even allow
     * yet while mandatory data is missing.
     */
    public openLinkOrderDialog(itemIndex: number): void {
        const invoiceId = this.existingInvoiceId();
        const supplierId = this.invoice()?.supplier?.id;
        const itemGroup = this.form.controls.items.controls[itemIndex];
        if (!invoiceId || !itemGroup) {
            return;
        }
        if (!supplierId) {
            this._notificationService.showNotification(
                marker('Bitte zuerst einen Lieferanten auswählen, bevor Bestellpositionen verknüpft werden können.'),
                NotificationMessageLevel.INFO,
            );
            return;
        }

        const current = itemGroup.controls.orderRelation.value;
        const dialogData: LinkOrderDialogData = {
            invoiceId,
            taskId: this.taskId(),
            header: { supplierCompanyId: supplierId, companyCodeId: this.resolveCompanyCodeId() ?? null },
            currentRelation: current
                ? {
                      orderId: current.orderId,
                      orderItemId: current.orderItemId,
                      orderItemPosition: current.orderItemPosition,
                      orderErpNumber: current.orderErpNumber,
                  }
                : undefined,
        };

        const isInvoiceItemEmpty = this.isInvoiceItemUnmodified(itemGroup);
        this._dialog
            .open<LinkOrderDialogComponent, LinkOrderDialogData, LinkOrderDialogResult>(
                LinkOrderDialogComponent,
                DialogOptions.MEDIUM(dialogData),
            )
            .afterClosed()
            .subscribe((result) => void this._applyOrderLinkResult(result, itemGroup, isInvoiceItemEmpty));
    }

    /**
     * Keeps the chosen order link in the item form and, like linking a delivery receipt, offers to take
     * over the order position's data: without asking for an untouched item, after a question otherwise.
     * Declining keeps the item's own data but links it all the same.
     */
    private async _applyOrderLinkResult(
        result: LinkOrderDialogResult | undefined,
        itemGroup: FormGroup<ICreateStandaloneInvoiceItemForm>,
        isInvoiceItemEmpty: boolean,
    ): Promise<void> {
        if (!result) {
            return;
        }
        if (result.remove) {
            itemGroup.controls.orderRelation.setValue(undefined);
            itemGroup.markAsDirty();
            return;
        }
        if (!result.relation) {
            return;
        }

        const takeOverData = await this._confirmDataTakeOver(
            isInvoiceItemEmpty,
            marker('Daten aus der Bestellung übernehmen?'),
            marker(
                'Sollen die Daten der Bestellposition auf die Rechnungsposition angewendet werden? Diese Aktion überschreibt die vorhandenen Daten.',
            ),
        );
        if (takeOverData) {
            const orderItem = await this._loadOrderItem(result.relation.orderItemId);
            if (orderItem) {
                this.applyDeliveryItemDataToInvoiceItem(itemGroup, CreateOrUpdateStandaloneInvoiceComponent._orderItemAsItemData(orderItem));
            }
        }
        itemGroup.controls.orderRelation.setValue(result.relation);
        itemGroup.markAsDirty();
    }

    private async _loadOrderItem(orderItemId: number): Promise<IOrderItemViewDto | null> {
        try {
            return await firstValueFrom(this._orderDataService.getOrderItemById(orderItemId));
        } catch (error) {
            console.warn('Failed to load order item data:', error);
            return null;
        }
    }

    /** The order position in the shape the delivery-receipt take-over already applies to an item. */
    private static _orderItemAsItemData(orderItem: IOrderItemViewDto): IMergedDeliveryItemData {
        return {
            id: orderItem.id,
            name: orderItem.name,
            shortDescription: orderItem.shortDescription,
            articleNumberSupplier: orderItem.articleNumber,
            quantity: String(orderItem.quantity),
            orderUnit: orderItem.orderUnit,
            contentUnit: orderItem.contentUnit,
            numberOfContentUnitsPerOrderUnit: orderItem.numberOfContentUnitsPerOrderUnit,
            unitPrice: orderItem.price,
            taxInPercent: orderItem.tax,
            costCenter: orderItem.costCenter,
            generalLedgerAccount: orderItem.account,
            project: orderItem.project,
            customAccountAssignment: orderItem.customAccountAssignments,
            manufacturerArticleNumber: orderItem.manufacturerArticleSku,
            materialGroupCode: orderItem.materialGroupCode,
        };
    }

    /** Only the identifying pair goes into the save; the position is display data the backend resolves itself. */
    private _toOrderRelationCommand(relation: IOrderRelation | undefined): IOrderRelation | undefined {
        return relation ? { orderId: relation.orderId, orderItemId: relation.orderItemId } : undefined;
    }

    /**
     * Opens the link delivery receipt dialog for a specific item
     */
    public openLinkDeliveryReceiptDialog(itemIndex: number): void {
        const dialogData = this._prepareDialogData(itemIndex);

        this._dialog
            .open<LinkDeliveryReceiptDialogComponent, LinkDeliveryReceiptDialogData, LinkDeliveryReceiptDialogResult>(
                LinkDeliveryReceiptDialogComponent,
                DialogOptions.MEDIUM(dialogData),
            )
            .afterClosed()
            .subscribe(async (result) => {
                await this._handleDialogResult(result, itemIndex, dialogData.isInvoiceItemEmpty);
            });
    }

    /**
     * Prepares the data needed for the link delivery receipt dialog
     */
    private _prepareDialogData(itemIndex: number): LinkDeliveryReceiptDialogData {
        const supplierId = this.invoice()?.supplier?.id;
        const itemsArray = this.form.controls.items.controls;

        const { alreadyLinkedReceipts, alreadyLinkedItemIds } = this._extractLinkedReceiptData(itemsArray);
        const currentRelation = this._getCurrentRelation(itemsArray, itemIndex);
        const currentItem = itemsArray[itemIndex];
        const isInvoiceItemEmpty = this.isInvoiceItemUnmodified(currentItem);

        return {
            supplierCompanyId: supplierId,
            supplierName: this.invoice()?.supplier?.name,
            supplierDisplayName: supplierDisplayName(
                this.invoice()?.supplier?.supplierNumber,
                this.invoice()?.supplier?.name ?? '',
            ),
            alreadyLinkedReceipts,
            alreadyLinkedItemIds,
            currentRelation,
            isInvoiceItemEmpty,
        };
    }

    /**
     * Extracts already linked receipt data from form items
     */
    private _extractLinkedReceiptData(itemsArray: FormGroup<ICreateStandaloneInvoiceItemForm>[]) {
        const alreadyLinkedMap = new Map<number, string>();
        const alreadyLinkedItemIds = new Set<number>();

        itemsArray.forEach((fg) => {
            const rel = fg.controls.deliveryRelation.value;
            if (rel) {
                alreadyLinkedMap.set(rel.deliveryReceiptId, rel.deliveryReceiptNumber);
                alreadyLinkedItemIds.add(rel.deliveryItemId);
            }
        });

        const alreadyLinkedReceipts = Array.from(alreadyLinkedMap.entries()).map(([receiptId, receiptNumber]) => ({
            receiptId,
            receiptNumber,
        }));

        return { alreadyLinkedReceipts, alreadyLinkedItemIds };
    }

    /**
     * Gets the current relation data for the specified item
     */
    private _getCurrentRelation(itemsArray: FormGroup<ICreateStandaloneInvoiceItemForm>[], itemIndex: number) {
        const currentRelationValue = itemsArray[itemIndex].controls.deliveryRelation.value;
        return currentRelationValue
            ? {
                  ...currentRelationValue,
                  supplierName: this.invoice()?.supplier?.name,
                  supplierNumber: this.invoice()?.supplier?.supplierNumber,
              }
            : undefined;
    }

    /**
     * Handles the result from the link delivery receipt dialog
     */
    private async _handleDialogResult(
        result: LinkDeliveryReceiptDialogResult | undefined,
        itemIndex: number,
        isInvoiceItemEmpty: boolean,
    ): Promise<void> {
        if (!result) return;

        const itemsArray = this.form.controls.items.controls;
        const currentItem = itemsArray[itemIndex];

        if (result.remove) {
            this._handleRemoveRelation(currentItem);
        } else if (result.relation && result.item) {
            await this._handleSetRelation(result, currentItem, isInvoiceItemEmpty);
        }
    }

    /**
     * Handles removing a delivery relation
     */
    private _handleRemoveRelation(currentItem: FormGroup<ICreateStandaloneInvoiceItemForm>): void {
        currentItem.controls.deliveryRelation.setValue(undefined);
    }

    /**
     * Handles setting a new delivery relation with data application
     */
    private async _handleSetRelation(
        result: LinkDeliveryReceiptDialogResult,
        currentItem: FormGroup<ICreateStandaloneInvoiceItemForm>,
        isInvoiceItemEmpty: boolean,
    ): Promise<void> {
        // "Nein" declines taking over the receipt data, not the link itself (EPROC-12024).
        const takeOverData = await this._checkDataOverwriteConfirmation(isInvoiceItemEmpty);
        if (takeOverData) {
            const mergedItemData = await this._loadAndMergeOrderItemData(result.item);
            this.applyDeliveryItemDataToInvoiceItem(currentItem, mergedItemData);
        }

        // Set the delivery relation
        currentItem.controls.deliveryRelation.setValue(result.relation);
    }

    /**
     * Checks if user confirms data overwrite for non-empty invoice items
     */
    private async _checkDataOverwriteConfirmation(isInvoiceItemEmpty: boolean): Promise<boolean> {
        return this._confirmDataTakeOver(
            isInvoiceItemEmpty,
            marker('Daten aus dem Lieferschein übernehmen?'),
            marker(
                'Sollen die Daten der Lieferschein Position auf die Rechnungsposition angewendet werden? Diese Aktion überschreibt die vorhandenen Daten.',
            ),
        );
    }

    /** Asks whether linked data may overwrite the item; an untouched item takes it over without asking. */
    private async _confirmDataTakeOver(isInvoiceItemEmpty: boolean, heading: string, question: string): Promise<boolean> {
        if (isInvoiceItemEmpty) {
            return true;
        }

        return await firstValueFrom(
            this._confirmationService.confirm(question, {
                heading,
                showCancelButton: true,
                confirmationButtonText: marker('Daten übernehmen'),
                confirmationButtonColor: BootstrapColors.PRIMARY,
                cancelButtonText: marker('Nein'),
                cancelButtonColor: BootstrapColors.SUCCESS,
                reverseButtonOrder: true,
            }),
        );
    }

    /**
     * Loads order item data and merges it with receipt item data
     */
    private async _loadAndMergeOrderItemData(receiptItem: any): Promise<IMergedDeliveryItemData> {
        const mergedItemData: IMergedDeliveryItemData = { ...receiptItem };

        // Load order item data if available
        if (receiptItem && receiptItem.orderItemId) {
            try {
                const orderItemData: IOrderItemViewDto = await firstValueFrom(
                    this._orderDataService.getOrderItemById(receiptItem.orderItemId),
                );

                // Merge order item data into the receipt item data
                if (orderItemData) {
                    // Map order item fields to the simplified IMergedDeliveryItemData
                    mergedItemData.unitPrice = orderItemData.price;
                    mergedItemData.taxInPercent = orderItemData.tax;
                    mergedItemData.costCenter = orderItemData.costCenter;
                    mergedItemData.generalLedgerAccount = orderItemData.account;
                    mergedItemData.project = orderItemData.project;
                    mergedItemData.customAccountAssignment = orderItemData.customAccountAssignments;
                    mergedItemData.manufacturerArticleNumber = orderItemData.manufacturerArticleSku;
                    mergedItemData.materialGroupCode = orderItemData.materialGroupCode;
                }
            } catch (error) {
                console.warn('Failed to load order item data:', error);
            }
        }

        return mergedItemData;
    }

    async onSaveDraft() {
        if (this.allowSavingWithMissingFields()) {
            if (this.requiresCompanyCodeSelection() || this.hasBlockingItemValidationErrors()) {
                this.form.markAllAsTouched();
                return;
            }
        } else if (this.formInvalidForStandardSave()) {
            this.form.markAllAsTouched();
            return;
        }
        const currentInvoice = this.invoice();

        const invoiceId = this.existingInvoiceId();

        const invoiceDate = this.formatIsoDateTimeOrNull(this.form.controls.head.controls.documentDate);
        const paymentDueDate = this.formatIsoDateTimeOrNull(this.form.controls.head.controls.paymentUntil);
        const paymentDate = this.formatIsoDateTimeOrNull(this.form.controls.head.controls.paymentDate);

        const command: ICreateInvoiceCommandDto = {
            invoiceNumber: this.form.controls.head.controls.invoiceNumber.value,
            companyCodeId: this.resolveCompanyCodeId(),
            sectionId: currentInvoice?.department?.index ?? null,
            supplierCompanyId: currentInvoice?.supplier?.id ?? null,
            invoiceDate,
            paymentDate,
            paymentUntil: paymentDueDate,
            invoiceAddressId: currentInvoice?.invoiceAddress?.id ?? undefined,
            comment: this.form.controls.comment.value,
            items: this.mapFormItemsToCommandItems(
                this.form.controls.items.controls,
                this.form.controls.head.controls.currency.value.value,
            ),
            charges: this.mapFormAdditionalCostsToCommandItems(
                this.form.controls.charges.controls,
                this.form.controls.head.controls.currency.value.value,
            ),
            remark: this.form.controls.head.controls.remark.value,
        };

        // Get the files and scanned images from the form
        const files = this.form.controls.head.controls.files.value;
        const scannedImages = this.form.controls.head.controls.scannedImages.value;

        // Call the store with the command and attachments
        await firstValueFrom(this._store.onSaveDraft(command, invoiceId, files, scannedImages, this._store.taskId()));
        if (this.insideDialog) {
            this.invoiceChanged.emit();
        }
    }

    async onSaveForApproval() {
        if (this.formInvalidForStandardSave()) {
            this.form.markAllAsTouched();
            return;
        }

        const currentInvoice = this.invoice();
        const invoiceId = this.existingInvoiceId();
        const invoiceDate = this.formatIsoDateTimeOrNull(this.form.controls.head.controls.documentDate);
        const paymentDueDate = this.formatIsoDateTimeOrNull(this.form.controls.head.controls.paymentUntil);
        const paymentDate = this.formatIsoDateTimeOrNull(this.form.controls.head.controls.paymentDate);

        const command: ICreateInvoiceCommandDto = {
            invoiceNumber: this.form.controls.head.controls.invoiceNumber.value,
            companyCodeId: this.resolveCompanyCodeId(),
            sectionId: currentInvoice.department?.index ?? null,
            supplierCompanyId: currentInvoice.supplier.id,
            invoiceDate,
            paymentDate,
            paymentUntil: paymentDueDate,
            invoiceAddressId: currentInvoice.invoiceAddress.id,
            comment: this.form.controls.comment.value,
            items: this.mapFormItemsToCommandItems(
                this.form.controls.items.controls,
                this.form.controls.head.controls.currency.value.value,
            ),
            charges: this.mapFormAdditionalCostsToCommandItems(
                this.form.controls.charges.controls,
                this.form.controls.head.controls.currency.value.value,
            ),
            remark: this.form.controls.head.controls.remark.value,
        };
        // Get the files and scanned images from the form
        const files = this.form.controls.head.controls.files.value;
        const scannedImages = this.form.controls.head.controls.scannedImages.value;

        // Call the store with the command and attachments
        await firstValueFrom(this._store.onSaveForApproval(command, invoiceId, files, scannedImages));
        if (this.insideDialog) {
            this.invoiceChanged.emit();
        }
    }

    /**
     * Save the invoice
     */
    public async save(): Promise<void> {
        if (this.formInvalidForStandardSave()) {
            this.form.markAllAsTouched();
            return;
        }

        const currentInvoice = this.invoice();
        if (!currentInvoice || !currentInvoice.supplier) {
            console.error('No supplier selected');
            return;
        }

        const invoiceDate = this.formatIsoDateTimeOrNull(this.form.controls.head.controls.documentDate);
        const paymentDate = this.formatIsoDateTimeOrNull(this.form.controls.head.controls.paymentDate);
        const paymentDueDate = this.formatIsoDateTimeOrNull(this.form.controls.head.controls.paymentUntil);

        // Map the form to the ICreateInvoiceCommandDto interface
        const command: ICreateInvoiceCommandDto = {
            invoiceNumber: this.form.controls.head.controls.invoiceNumber.value,
            companyCodeId: this.resolveCompanyCodeId(),
            sectionId: currentInvoice?.department?.index ?? null,
            supplierCompanyId: currentInvoice.supplier.id,
            invoiceDate,
            paymentDate,
            paymentUntil: paymentDueDate,
            invoiceAddressId: currentInvoice.invoiceAddress.id,
            comment: this.form.controls.comment.value,
            items: this.mapFormItemsToCommandItems(
                this.form.controls.items.controls,
                this.form.controls.head.controls.currency.value.value,
            ),
            charges: this.mapFormAdditionalCostsToCommandItems(
                this.form.controls.charges.controls,
                this.form.controls.head.controls.currency.value.value,
            ),

            remark: this.form.controls.head.controls.remark.value,
            creationType:
                this._store.receiptIds().length === 0 ? InvoiceCreationType.MANUAL : InvoiceCreationType.DELIVERY_NOTE,
        };

        // Get the files and scanned images from the form
        const files = this.form.controls.head.controls.files.value;
        const scannedImages = this.form.controls.head.controls.scannedImages.value;

        Logger.info('invoice creation command', command);

        // Call the store with the command and attachments
        await firstValueFrom(this._store.saveInvoice(command, files, scannedImages));
        if (this.insideDialog) {
            this.invoiceChanged.emit();
        }
    }

    /**
     * Maps form items to command items
     * @param formItems The form items to map
     * @param currency The currency to use for the price
     * @returns The mapped command items
     */
    private mapFormItemsToCommandItems(
        formItems: FormGroup<ICreateStandaloneInvoiceItemForm>[],
        currency: string,
    ): ICreateInvoiceItemCommandDto[] {
        return formItems.map((item, index) => {
            // Create the base command item
            const commandItem: ICreateInvoiceItemCommandDto = {
                name: item.controls.name.value,
                description: item.controls.description.value,
                supplierArticleNumber: item.controls.supplierArticleNumber.value,
                quantity: {
                    amount: item.controls.quantity.value,
                    unit: item.controls.productUnit.value,
                },
                price: {
                    amount: item.controls.unitPrice.value,
                    currency: currency,
                },
                contentUnit: item.controls.contentUnit.value,
                contentUnitsPerOrderUnit: item.controls.contentUnitsPerOrderUnit.value,
                discountPercentage: item.controls.discountPercentage.value,
                taxInPercent: item.controls.taxInPercent.value,
                priceQuantity: item.controls.priceQuantity.value || 1,
                materialGroupCode: item.controls.materialGroupCode.value || undefined,
                deliveryRelation: item.controls.deliveryRelation.value,
                orderRelation: this._toOrderRelationCommand(item.controls.orderRelation.value),
                changeAccountingData: this.accountingChangeData.has(index)
                    ? this.accountingChangeData
                          .get(index)
                          .filter((e) => e.accountAssignmentId !== null && e.accountAssignmentId !== undefined)
                          .map((a) => new UpdateAccountingRequestDto(a))
                    : [],
            };

            return commandItem;
        });
    }

    private mapFormAdditionalCostsToCommandItems(
        formItems: FormGroup<ICreateInvoiceChargesForm>[],
        currency: string,
    ): CreateInvoiceCharge[] {
        return formItems.map((item) => {
            const amount = item.controls.price.value;
            const tax = item.controls.taxInPercent.value;

            const commandItem: CreateInvoiceCharge = {
                name: item.controls.name.value,
                description: item.controls.description.value,
                articleNumberSupplier: item.controls.articleNumberSupplier.value,
                articleNumberCustomer: item.controls.articleNumberCustomer.value,
                price: {
                    amount: amount,
                    currency: currency,
                },
                internalNote: item.controls.internalNote.value,
                taxInPercent: tax,
                chargeType: item.controls.chargeType.value,
            };

            return commandItem;
        });
    }

    /**
     * Checks if an invoice item is still in its default unmodified state
     */
    private isInvoiceItemUnmodified(itemForm: FormGroup<ICreateStandaloneInvoiceItemForm>): boolean {
        const controls = itemForm.controls;

        // Check if the item has default values (indicating it hasn't been modified by user)
        return (
            controls.name.value === '' &&
            controls.description.value === '' &&
            controls.supplierArticleNumber.value === '' &&
            controls.unitPrice.value === 0 &&
            (controls.quantity.value === 1 || controls.quantity.value === 0) && // Allow both default new item (1) and empty (0)
            controls.productUnit.value === 'C62' &&
            controls.contentUnit.value === 'C62' &&
            controls.materialGroupCode.value === '' &&
            controls.contentUnitsPerOrderUnit.value === 1 &&
            controls.priceQuantity.value === 1
        );
    }

    /**
     * Applies delivery receipt item data to an invoice item using data from the dialog
     */
    private applyDeliveryItemDataToInvoiceItem(
        itemForm: FormGroup<ICreateStandaloneInvoiceItemForm>,
        deliveryItem: IMergedDeliveryItemData,
    ): void {
        // Apply basic delivery item data to invoice item
        itemForm.patchValue({
            name: deliveryItem.name || '',
            description: deliveryItem.shortDescription || '',
            supplierArticleNumber: deliveryItem.articleNumberSupplier || '',
            quantity: parseFloat(deliveryItem.quantity) || 1,
            productUnit: deliveryItem.orderUnit || 'C62',
            contentUnit: deliveryItem.contentUnit || 'C62',
            contentUnitsPerOrderUnit: deliveryItem.numberOfContentUnitsPerOrderUnit || 1,
        });

        // Apply price information from order item data if available
        if (deliveryItem.unitPrice !== undefined) {
            itemForm.patchValue({
                unitPrice: deliveryItem.unitPrice,
            });
        }
        if (deliveryItem.taxInPercent !== undefined && deliveryItem.taxInPercent !== null) {
            itemForm.patchValue({
                taxInPercent: deliveryItem.taxInPercent,
            });
        }
        // The line total and the sums show the stored values until a pricing field counts as edited;
        // taken-over values are an edit, otherwise the totals would stay at the old amounts (EPROC-12024).
        itemForm.controls.quantity.markAsDirty();
        itemForm.controls.unitPrice.markAsDirty();
        itemForm.controls.taxInPercent.markAsDirty();

        if (deliveryItem.materialGroupCode) {
            itemForm.patchValue({
                materialGroupCode: deliveryItem.materialGroupCode,
            });
        }

        // Apply accounting information from order item data if available
        this._applyAccountingDataToInvoiceItem(itemForm, deliveryItem);

        // Update form validity after applying data
        itemForm.updateValueAndValidity();
    }

    /**
     * Applies accounting information from merged order item data to the invoice item
     */
    private _applyAccountingDataToInvoiceItem(
        itemForm: FormGroup<ICreateStandaloneInvoiceItemForm>,
        deliveryItem: IMergedDeliveryItemData,
    ): void {
        // Find the index of this item to update its accounting input
        const itemsArray = this.form.controls.items.controls;
        const itemIndex = itemsArray.findIndex((item) => item === itemForm);

        if (itemIndex === -1) {
            return; // Item not found in array
        }

        // Initialize accounting input if not already done
        this.initAccountingInput(itemIndex);

        // Apply cost center if available
        if (deliveryItem.costCenter) {
            const change = AccountingChange.COST_CENTER(this._accountingFromOrderKey(deliveryItem.costCenter));
            this.emitAccountingChanged(change, itemIndex);
        }

        // Apply general ledger account if available
        if (deliveryItem.generalLedgerAccount) {
            const change = AccountingChange.GENERAL_LEDGER_ACCOUNT(
                this._accountingFromOrderKey(deliveryItem.generalLedgerAccount),
            );
            this.emitAccountingChanged(change, itemIndex);
        }

        // Apply project if available
        if (deliveryItem.project) {
            const change = AccountingChange.PROJECT(this._accountingFromOrderKey(deliveryItem.project));
            this.emitAccountingChanged(change, itemIndex);
        }

        // Apply custom account assignments from the underlying order item
        for (const custom of deliveryItem.customAccountAssignment ?? []) {
            const typeId = custom.type?.id;
            if (typeId == null || !custom.code) {
                continue;
            }
            const change = AccountingChange.CUSTOM(
                typeId,
                new Accounting(custom.id ?? 0, custom.code, custom.name ?? custom.code, ''),
            );
            this.emitAccountingChanged(change, itemIndex);
        }
    }

    /**
     * Builds an {@link Accounting} from an order item accounting key (cost center, general ledger account or
     * project). The order item only carries the plain key for these base accountings and the backend resolves
     * them by key, so the id is irrelevant here. We use 0 (instead of an undefined id) so the resulting change
     * is not discarded as "empty" by the id-based filter when the invoice is saved.
     */
    private _accountingFromOrderKey(key: string): Accounting {
        return new Accounting(0, key, key, '');
    }

    /**
     * Opens the supplier selection dialog
     */
    public selectSupplier(): void {
        if (this.showReceiptEditingRestrictionNotice()) {
            return;
        }

        if (!this.fixedSuppler()) {
            // Get the current supplier ID if available
            const currentInvoice = this.invoice();
            const currentSupplierId = currentInvoice?.supplier?.id ? [currentInvoice.supplier.id] : [];
            const companyCodeId = this.resolveCompanyCodeId();
            if (!companyCodeId) {
                return;
            }

            // Open the supplier selection dialog with singleSelection=true and selectContacts=false
            this._supplierService
                .selectSupplier(currentSupplierId, false, false, true, companyCodeId)
                .pipe(take(1))
                .subscribe((result) => {
                    if (result && result.length > 0) {
                        const selectedSupplier = result[0];
                        const newSupplierId = selectedSupplier.company.id;

                        // Check if supplier actually changed
                        if (currentInvoice?.supplier?.id !== newSupplierId) {
                            // Clear all delivery receipt relations when supplier changes
                            this._clearAllDeliveryReceiptRelations();
                            // Same for order links: the save would drop a link to another supplier's order.
                            this._clearAllOrderRelations();
                        }

                        // Update the supplier in the store
                        this._store.updateSupplier({
                            id: newSupplierId,
                            name: selectedSupplier.company.name,
                            supplierNumber: selectedSupplier.company.supplierNumber,
                        });
                    }
                });
        }
    }

    /**
     * Opens the departmant selection dialog
     */
    public selectDepartmant(): void {
        if (!this.fixedDepartment()) {
            const currentInvoice = this.invoice();
            const currentDepartmentId = currentInvoice?.department?.index ? [currentInvoice.department.index] : [];

            this._departmentService
                .selectDepartment(currentDepartmentId)
                .pipe(take(1))
                .subscribe((result) => {
                    if (result) {
                        this._store.updateDepartment(result);
                    }
                });
        }
    }

    /**
     * Add a new empty item to the form
     */
    public addItem(): void {
        const items: FormArray<FormGroup<ICreateStandaloneInvoiceItemForm>> = this.form.controls.items;
        const emptyDeliveryRelationCtrl = new FormControl<IDeliveryRelation | undefined>(undefined);

        const formGroup = this._formBuilder.group<ICreateStandaloneInvoiceItemForm>({
            id: new FormControl('', { nonNullable: true }),
            supplierArticleNumber: new FormControl('', { nonNullable: true }),
            name: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
            description: new FormControl('', { nonNullable: true }),
            materialGroupCode: new FormControl('', { nonNullable: true }),
            productUnit: new FormControl('C62', {
                nonNullable: true,
                validators: [Validators.required],
            }),
            contentUnitsPerOrderUnit: new FormControl(1, {
                nonNullable: true,
                validators: [Validators.min(0)],
            }),
            contentUnit: new FormControl('C62', {
                nonNullable: true,
            }),
            unitPrice: new FormControl(0, { nonNullable: true }),
            quantity: new FormControl(1, {
                nonNullable: true,
                validators: [Validators.required, Validators.min(0)],
            }),
            discountPercentage: new FormControl(0, {
                nonNullable: true,
                validators: [Validators.min(0), Validators.max(100)],
            }),
            taxInPercent: new FormControl(19, {
                nonNullable: true,
                validators: [Validators.min(0)],
            }),
            priceQuantity: new FormControl(1, {
                nonNullable: true,
                validators: [Validators.min(1)],
            }),
            deliveryRelation: emptyDeliveryRelationCtrl,
            orderRelation: new FormControl<IOrderRelation | undefined>(undefined),
        });
        items.push(formGroup);
        this.updateMaterialGroupValidators();

        // Initialize accounting input for the new item
        const itemIndex = items.length - 1;
        this.initAccountingInput(itemIndex);
        // Seed as valid; the accounting component emits the real state once it has validated.
        this.accountingValidMap.set(itemIndex, true);
        this.materialGroupValidMap.set(itemIndex, !this.isMaterialGroupRequired());

        // Update form validity
        this.form.updateValueAndValidity();
    }

    public addAdditionalCost(): void {
        if (this.receiptBasedPriceFieldsReadOnly()) {
            return;
        }

        const additionalCost: FormArray<FormGroup<ICreateInvoiceChargesForm>> = this.form.controls.charges;

        // Get the current main invoice currency
        const mainCurrency = this.form.controls.head.controls.currency.value || new IimFilterableRecord<string>('EUR');

        const formGroup = this._formBuilder.group<ICreateInvoiceChargesForm>({
            articleNumberCustomer: new FormControl('', { nonNullable: true }),
            articleNumberSupplier: new FormControl('', { nonNullable: true }),
            description: new FormControl('', { nonNullable: true }),
            name: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
            price: new FormControl(0, { nonNullable: true }),
            currency: new FormControl(mainCurrency),
            taxInPercent: new FormControl(19, {
                nonNullable: true,
                validators: [Validators.min(0)],
            }),
            internalNote: new FormControl('', { nonNullable: true }),
            chargeType: new FormControl(ChargeTypeEnum.SHIPPING, { nonNullable: true }),
        });
        additionalCost.push(formGroup);

        // Update form validity
        this.form.updateValueAndValidity();
    }

    /**
     * Remove an item from the form
     * @param index The index of the item to remove
     */
    public removeItem(index: number): void {
        // Get the items FormArray
        const items: FormArray<FormGroup<ICreateStandaloneInvoiceItemForm>> = this.form.controls.items;

        // Check if the index is valid
        if (index >= 0 && index < items.length) {
            // Remove the item from the FormArray
            items.removeAt(index);

            // Remove the corresponding accounting input
            if (index < this.accountingInputs.length) {
                this.accountingInputs.splice(index, 1);
            }

            const newMap = new Map<number, boolean>();
            Array.from(this.accountingValidMap.entries())
                .filter(([i]) => i !== index)
                .sort(([a], [b]) => a - b)
                .forEach(([i, value]) => {
                    const adjustedIndex = i > index ? i - 1 : i;
                    newMap.set(adjustedIndex, value);
                });
            this.accountingValidMap = newMap;

            const newMaterialGroupValidMap = new Map<number, boolean>();
            Array.from(this.materialGroupValidMap.entries())
                .filter(([i]) => i !== index)
                .sort(([a], [b]) => a - b)
                .forEach(([i, value]) => {
                    const adjustedIndex = i > index ? i - 1 : i;
                    newMaterialGroupValidMap.set(adjustedIndex, value);
                });
            this.materialGroupValidMap = newMaterialGroupValidMap;

            this.recommendedMaterialGroupCodesByIndex.splice(index, 1);
            this.recommendedMaterialGroupSourceLabelsByIndex.splice(index, 1);
            this.recommendedMaterialGroupReasonLabelsByIndex.splice(index, 1);
            this.reindexPendingAutofill(this.pendingGeneralLedgerAutofill, index);
            this.reindexPendingAutofill(this.pendingMaterialGroupAutofill, index);

            // Update form validity
            this.form.updateValueAndValidity();
        }
    }

    public removeCharge(index: number): void {
        if (this.receiptBasedPriceFieldsReadOnly()) {
            return;
        }

        // Get the items FormArray
        const charges: FormArray<FormGroup<ICreateInvoiceChargesForm>> = this.form.controls.charges;

        // Check if the index is valid
        if (index >= 0 && index < charges.length) {
            // Remove the item from the FormArray
            charges.removeAt(index);
        }

        // Update form validity
        this.form.updateValueAndValidity();
    }

    /**
     * Opens the file selection dialog
     */
    addAttachment(): void {
        const options: IUploadBottomSheetOptions = {
            uploadInternalPossible: false,
            multiple: true,
        };
        if (this.invoiceSettings().attachment.restrictToAcceptedMimeTypes) {
            options.acceptedMimeTypes = this.invoiceSettings().attachment.acceptedMimeTypes;
        }
        this._bottomSheetService
            .upload(options)
            .afterDismissed()
            .subscribe((result: UploadBottomSheetResult) => {
                if (result && result.formData.has('files')) {
                    const currentFiles: FormData = this.form.controls.head.controls.files.value;
                    const newFiles: File[] = [];
                    let hasPdf = false;
                    result.formData.getAll('files').forEach((file: File) => {
                        currentFiles.append('files', file);
                        newFiles.push(file);
                        // Check if any of the added files is a PDF
                        if (
                            file instanceof File &&
                            (file.type === 'application/pdf' || file.name?.toLowerCase().endsWith('.pdf'))
                        ) {
                            hasPdf = true;
                        }
                    });

                    // Update files signal immediately with new files
                    const existingFiles = this.files();
                    this.files.set([...existingFiles, ...newFiles]);

                    // Then update form control (this will trigger valueChanges but we already updated files signal)
                    this.form.controls.head.controls.files.patchValue(currentFiles);

                    // Automatically show PDF preview if a PDF was added (like in OrderConfirmationBasicApprovalFormComponent)
                    // Use setTimeout to ensure computed signals are recalculated
                    if (hasPdf && !this.isMobile()) {
                        setTimeout(() => {
                            if (this.hasPdfFileAttachments()) {
                                this.displayPdfPreview.set(true);
                            }
                        }, 100);
                    }
                }
                if (result && result.formData.has('images')) {
                    const currentFiles: FormData = this.form.controls.head.controls.scannedImages.value;
                    currentFiles.delete('images');
                    result.formData.getAll('images').forEach((file: File) => {
                        currentFiles.append('images', file);
                    });
                    this.form.controls.head.controls.scannedImages.patchValue(currentFiles);
                }
            });
    }

    /**
     * Removes a file from the FormData
     * @param fileToRemove The file to remove
     */
    removeFile(fileToRemove: FormDataEntryValue): void {
        if (!(fileToRemove instanceof File)) {
            console.warn('Cannot remove non-File entry from FormData');
            return;
        }

        const currentFiles = this.form.controls.head.controls.files.value;
        const newFormData = new FormData();

        // Get all files from the current FormData
        const files = currentFiles.getAll('files');

        // Add all files except the one to be removed to the new FormData
        files.forEach((file: FormDataEntryValue) => {
            if (file instanceof File && file !== fileToRemove) {
                newFormData.append('files', file);
            }
        });

        // Update the form control with the new FormData
        this.form.controls.head.controls.files.setValue(newFormData);

        // Validate the form
        this.form.controls.head.updateValueAndValidity();
    }

    /**
     * Removes a scanned image from the FormData
     * @param imageToRemove The image to remove
     */
    removeScannedImage(imageToRemove: FormDataEntryValue): void {
        if (!(imageToRemove instanceof File)) {
            console.warn('Cannot remove non-File entry from FormData');
            return;
        }

        const currentImages = this.form.controls.head.controls.scannedImages.value;
        const newFormData = new FormData();

        // Get all images from the current FormData
        const images = currentImages.getAll('images');

        // Add all images except the one to be removed to the new FormData
        images.forEach((image: FormDataEntryValue) => {
            if (image instanceof File && image !== imageToRemove) {
                newFormData.append('images', image);
            }
        });

        // Update the form control with the new FormData
        this.form.controls.head.controls.scannedImages.setValue(newFormData);

        // Validate the form
        this.form.controls.head.updateValueAndValidity();
    }

    /**
     * Handles accounting changes
     * @param event The accounting change event
     * @param itemIndex The index of the item that changed
     */
    emitAccountingChanged(event: AccountingChange, itemIndex: number): void {
        const entry = this.accountingChangeData.get(itemIndex) || [];
        // remove the old accounting change data of same type
        const updatedAccountingChanges = entry.filter((e) => e.id !== event.id); // remove old changes of the same type
        updatedAccountingChanges.push(event);

        // add the new accounting change data
        this.accountingChangeData.set(itemIndex, updatedAccountingChanges);
        this.accountingInputs[itemIndex] = this.accountingInputs[itemIndex].applyEvent(event);
        this.updateAutofillStateForAccountingChange(event, itemIndex);
        if (this.insideDialog) {
            this.invoiceChanged.emit();
        }
    }

    /**
     * Called when accounting is initialized
     */
    onAccountingInit(): void {
        this.accountingInitialized.emit();
    }

    /**
     * Handles accounting validation changes
     * @param isValid Whether the accounting fields are valid
     * @param index
     */
    onAccountingValidationChange(isValid: boolean, index: number): void {
        this.accountingValidMap.set(index, isValid);
        const allValid =
            Array.from(this.accountingValidMap.values()).every((v) => v === true) &&
            Array.from(this.materialGroupValidMap.values()).every((v) => v === true);
        this.accountingValidationChange.emit(allValid);
        this.recomputeRequiredFieldState();
    }

    onMaterialGroupValueChange(materialGroupCode: string | undefined, itemIndex: number): void {
        if (materialGroupCode) {
            if (!this.accountingInputs[itemIndex]?.generalLedgerAccountKey) {
                this.pendingGeneralLedgerAutofill.add(itemIndex);
            }
        } else {
            this.pendingGeneralLedgerAutofill.delete(itemIndex);
        }

        this.pendingMaterialGroupAutofill.delete(itemIndex);
    }

    onMaterialGroupValidationChange(isValid: boolean, index: number): void {
        this.materialGroupValidMap.set(index, isValid);
        const allValid =
            Array.from(this.accountingValidMap.values()).every((v) => v === true) &&
            Array.from(this.materialGroupValidMap.values()).every((v) => v === true);
        this.accountingValidationChange.emit(allValid);
        this.recomputeRequiredFieldState();
    }

    updateGeneralLedgerCandidates(itemIndex: number, candidates: readonly Accounting[]): void {
        if (!this.pendingGeneralLedgerAutofill.has(itemIndex)) {
            return;
        }

        if (this.accountingInputs[itemIndex]?.generalLedgerAccountKey || candidates.length !== 1) {
            return;
        }

        this.pendingGeneralLedgerAutofill.delete(itemIndex);
        this.emitAccountingChanged(AccountingChange.GENERAL_LEDGER_ACCOUNT(candidates[0]), itemIndex);
        this._changeDetectorRef.markForCheck();
    }

    // The candidate flow is fully code-based: the codes feed both the recommendation chips and the
    // single-candidate autofill (the form value is a material group CODE per the CVA contract).
    updateMaterialGroupCandidateCodes(itemIndex: number, candidateCodes: readonly string[]): void {
        this.recommendedMaterialGroupCodesByIndex[itemIndex] = candidateCodes.length > 0 ? candidateCodes : null;

        if (!this.pendingMaterialGroupAutofill.has(itemIndex)) {
            return;
        }

        const materialGroupControl = this.form.controls.items.controls[itemIndex]?.controls.materialGroupCode;
        if (!materialGroupControl || materialGroupControl.value || candidateCodes.length !== 1) {
            return;
        }

        this.pendingMaterialGroupAutofill.delete(itemIndex);
        materialGroupControl.setValue(candidateCodes[0]);
        materialGroupControl.markAsDirty();
        materialGroupControl.markAsTouched();
        this._changeDetectorRef.markForCheck();
    }

    updateMaterialGroupRecommendationSourceLabel(itemIndex: number, sourceLabel: string | null): void {
        this.recommendedMaterialGroupSourceLabelsByIndex[itemIndex] = sourceLabel;
    }

    updateMaterialGroupRecommendationReasonLabel(itemIndex: number, reasonLabel: string | null): void {
        this.recommendedMaterialGroupReasonLabelsByIndex[itemIndex] = reasonLabel;
    }

    /**
     * Initializes accounting inputs for an item
     * @param index The index of the item
     * @param input The accounting selection input to initialize
     */
    initAccountingInput(index: number, input: AccountingSelectionInput = new AccountingSelectionInput()): void {
        if (!this.accountingInputs[index]) {
            this.accountingInputs[index] = input;
        }
    }

    private primeRecommendationAutofill(index: number): void {
        const itemGroup = this.form.controls.items.controls[index];
        if (!itemGroup) {
            return;
        }

        const materialGroupCode = itemGroup.controls.materialGroupCode.value;
        const generalLedgerKey = this.accountingInputs[index]?.generalLedgerAccountKey ?? '';

        if (materialGroupCode && !generalLedgerKey) {
            this.pendingGeneralLedgerAutofill.add(index);
        }

        if (generalLedgerKey && !materialGroupCode) {
            this.pendingMaterialGroupAutofill.add(index);
        }
    }

    private updateAutofillStateForAccountingChange(event: AccountingChange, itemIndex: number): void {
        if (event.type !== AccountingType.GENERAL_LEDGER_ACCOUNT) {
            return;
        }

        if (event.value?.key) {
            const materialGroupCode = this.form.controls.items.controls[itemIndex]?.controls.materialGroupCode.value;
            if (!materialGroupCode) {
                this.pendingMaterialGroupAutofill.add(itemIndex);
            }
        } else {
            this.pendingMaterialGroupAutofill.delete(itemIndex);
        }

        this.pendingGeneralLedgerAutofill.delete(itemIndex);
    }

    private reindexPendingAutofill(indices: Set<number>, removedIndex: number): void {
        const reindexed = Array.from(indices)
            .filter((index) => index !== removedIndex)
            .map((index) => (index > removedIndex ? index - 1 : index));

        indices.clear();
        reindexed.forEach((index) => indices.add(index));
    }

    isAccountingFieldEmpty(changes: AccountingChange, input: AccountingSelectionInput) {
        switch (changes.type) {
            case AccountingType.COST_CENTER:
                return !input?.costCenterKey; // '' → leer
            case AccountingType.GENERAL_LEDGER_ACCOUNT:
                return !input?.generalLedgerAccountKey;
            case AccountingType.PROJECT:
                return !input?.projectAccountKey;
            case AccountingType.CUSTOM: {
                const typeId = changes.customAccountingTypeId;
                if (typeId == null) return false;
                const current = input?.getCustomAccounting(typeId) ?? '';
                return !current; // '' → leer
            }
        }
    }

    setAllAccountings() {
        if (this.receiptBasedAccountingFieldsReadOnly()) {
            return;
        }

        this._accountSelection
            .openAccountingChangeDialogWithSelection({
                editCostCenter: true,
                editProject: true,
                editGeneralLedgerAccount: true,
                editCustomAccountAssignment: true,
                companyCodeId: this.resolveCompanyCodeId() ?? '',
                // EPROC-11017: invoices have neither classification nor site bulk support.
                hideClassification: true,
                hideSite: true,
            })
            .subscribe((selection) => {
                if (selection.changes.length === 0) {
                    return;
                }
                let positionIndex = 0;
                for (const accountingInput of this.accountingInputs) {
                    for (const change of selection.changes) {
                        if (selection.overwriteExisting || this.isAccountingFieldEmpty(change, accountingInput)) {
                            this.emitAccountingChanged(change, positionIndex);
                            this._changeDetectorRef.markForCheck();
                        }
                    }
                    positionIndex++;
                }
            });
    }

    editDeliveryRelation(itemIndex: number) {
        const items = this.form.controls.items.controls;
        if (itemIndex < 0 || itemIndex >= items.length) {
            return;
        }
        if (this.showReceiptEditingRestrictionNotice() && items[itemIndex].controls.deliveryRelation.value) {
            return;
        }
        // Reuse the existing dialog open method which handles preselection and suggestions
        this.openLinkDeliveryReceiptDialog(itemIndex);
    }

    /** Removes the order link of one item from the form, like the trash icon of a delivery receipt link. */
    removeOrderRelation(itemIndex: number): void {
        const itemGroup = this.form.controls.items.controls[itemIndex];
        if (!itemGroup) {
            return;
        }
        itemGroup.controls.orderRelation.setValue(undefined);
        itemGroup.markAsDirty();
    }

    removeDeliveryRelation(itemIndex: number) {
        const items = this.form.controls.items.controls;
        if (itemIndex < 0 || itemIndex >= items.length) {
            return;
        }
        if (this.showReceiptEditingRestrictionNotice() && items[itemIndex].controls.deliveryRelation.value) {
            return;
        }
        // Clear the delivery relation for the item
        items[itemIndex].controls.deliveryRelation.setValue(undefined);
        // Update form validity after change
        this.form.updateValueAndValidity();
    }

    /**
     * Fetches supplier name for a delivery receipt and updates the cache
     */
    private _fetchSupplierName(receiptId: number): void {
        this._deliveryReceiptDataService
            .getById(receiptId)
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe({
                next: (response: any) => {
                    const supplierName = response.data?.supplier?.companyAddress?.companyName?.line1 || '';
                    const currentCache = this._supplierCache();
                    const newCache = new Map(currentCache);
                    newCache.set(receiptId, supplierName);
                    this._supplierCache.set(newCache);
                },
                error: (error) => {
                    console.error('Failed to fetch supplier name for receipt', receiptId, error);
                },
            });
    }

    /**
     * Gets supplier name for a delivery receipt from cache
     */
    getSupplierNameForDeliveryRelation(receiptId: number): string | null {
        const cache = this._supplierCache();
        return cache.get(receiptId) || null;
    }

    /**
     * Clears all delivery receipt relations from all invoice items
     * This is called when the supplier changes to ensure no cross-supplier relations exist
     */
    private _clearAllOrderRelations(): void {
        this.form.controls.items.controls.forEach((itemForm) => itemForm.controls.orderRelation.setValue(undefined));
    }

    private _clearAllDeliveryReceiptRelations(): void {
        const itemsArray = this.form.controls.items;

        // Iterate through all items and clear their delivery relations
        itemsArray.controls.forEach((itemForm) => {
            itemForm.controls.deliveryRelation.setValue(undefined);
        });

        // Update form validity after clearing relations
        this.form.updateValueAndValidity();
    }
}
